import React from "react";
import type { SkinnyBanner } from "@shared/graphql.types";
import { useSkinnyBannerItem } from "../Banner/useSkinnyBannerItem";
import { smallprintContainer } from "./Smallprint.css";
import { useFeatureFlag } from "../../../hooks";
import { ExperimentFlags } from "@shared/utils.experimentation";
import { usePromotionalBanner } from "packages/membership/components/PromotionalBanner/usePromotionalBanner";
import type { PromotionalBannerResponse } from "packages/membership/components/PromotionalBanner/types";
import { MarkdownContainer, Typography } from "@shared/ui.primitives";

type Props = {
  skinnyBannerData: SkinnyBanner[];
};

const getPromotionalBannerSmallPrint = (
  promotionalBannerDetails: PromotionalBannerResponse
): string | null | undefined => {
  const contentMessages = promotionalBannerDetails.contentMessages;

  return contentMessages.find(
    (message) =>
      [
        "FREE_SHIPPING_AND_SAMPLES_MEMBERS",
        "FREE_SHIPPING_NON_MEMBERS"
      ].includes(message.type) && message.smallPrint
  )?.smallPrint;
};

export const Smallprint: React.FC<Props> = ({ skinnyBannerData }) => {
  const skinnyBannerItem = useSkinnyBannerItem(skinnyBannerData);
  const { promotionalBannerDetails, loading: promoLoading } =
    usePromotionalBanner();
  const [loading, freeShippingFeatureFlag] = useFeatureFlag(
    ExperimentFlags.USE_FREE_SHIPPING_THRESHOLD,
    "0"
  );

  if (!promotionalBannerDetails) {
    // Intentional return so that the SkinnyBanner smallprint can be evaluated
    return;
  }

  if (!skinnyBannerItem?.smallPrint) {
    return null;
  }

  if (loading || promoLoading || freeShippingFeatureFlag !== "0") {
    return null;
  }

  const { title, smallPrint } = skinnyBannerItem;

  const smallPrintContent =
    getPromotionalBannerSmallPrint(promotionalBannerDetails) ?? smallPrint;

  return (
    <div key={title} className={smallprintContainer} data-testid="Smallprint">
      <MarkdownContainer
        content={smallPrintContent}
        forceBlock
        tagOverrides={{
          a: {
            component: Typography,
            props: {
              as: "a",
              underline: true,
              variant: "smallprint-md"
            }
          },
          p: {
            component: Typography,
            props: {
              as: "p",
              variant: "smallprint-md"
            }
          }
        }}
      />
    </div>
  );
};
